/**
 * SetupPassword page component
 *
 * This page allows first-time onboarding users to set up their password
 * using a magic link token received via email.
 *
 * Flow:
 * 1. User clicks magic link in email
 * 2. Frontend extracts token from URL query params
 * 3. User enters new password (with confirmation)
 * 4. Frontend calls POST /onboarding-auth/setup-password
 * 5. On success, sign in with Firebase custom token
 * 6. Redirect to Workday onboarding page (/onboarding/workday)
 */

import { Visibility, VisibilityOff } from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  FormControl,
  FormHelperText,
  FormLabel,
  IconButton,
  Input,
  Typography
} from "@mui/joy";
import { createRoute, useNavigate, useSearch } from "@tanstack/react-router";
import { signInWithCustomToken } from "firebase/auth";
import React, { useEffect, useState } from "react";
import { rootRoute } from "../../app";
import { auth } from "../../firebase.config";
import {
  useSetupPassword,
  useVerifyMagicLinkToken
} from "../../shared/api/magic-link.hooks";
import {
  ContactRole,
  useOnboardingStore,
  useUserStore
} from "../../shared/store";

// Password validation constants (must match backend)
const PASSWORD_MIN_LENGTH = 12;

interface PasswordValidation {
  isValid: boolean;
  errors: string[];
}

/**
 * Validates password meets complexity requirements
 * Matches the backend validation in onboarding-auth.controller.ts
 */
function validatePassword(password: string): PasswordValidation {
  const errors: string[] = [];

  if (!password || password.length < PASSWORD_MIN_LENGTH) {
    errors.push(`Password must be at least ${PASSWORD_MIN_LENGTH} characters`);
  }

  if (!/[A-Z]/.test(password)) {
    errors.push("Password must contain at least one uppercase letter");
  }

  if (!/[a-z]/.test(password)) {
    errors.push("Password must contain at least one lowercase letter");
  }

  if (!/[0-9]/.test(password)) {
    errors.push("Password must contain at least one number");
  }

  if (!/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?`~]/.test(password)) {
    errors.push("Password must contain at least one special character");
  }

  return {
    isValid: errors.length === 0,
    errors
  };
}

interface SetupPasswordSearch {
  token?: string;
}

export const SetupPassword: React.FC = () => {
  const navigate = useNavigate();
  const search = useSearch({ from: "/setup-password" }) as SetupPasswordSearch;
  const token = search?.token;

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);

  const setupPassword = useSetupPassword();
  const verifyToken = useVerifyMagicLinkToken();
  const { setUuid, clearOnboarding } = useOnboardingStore();
  const [isVerifying, setIsVerifying] = useState(true);

  // Verify the magic link token on page load
  // If invalid/expired, redirect to login immediately with message stored in sessionStorage
  // (EmailPasswordLogin reads from sessionStorage to display the message)
  useEffect(() => {
    if (token) {
      verifyToken.mutate(
        { token },
        {
          onSuccess: (data) => {
            if (!data.valid) {
              // Store error message in sessionStorage for the login page to display
              const message = data.passwordAlreadySet
                ? "Your password has already been set. Please log in with your password."
                : "This link has expired. Please log in or contact your administrator for a new link.";
              sessionStorage.setItem("onboardingExpiredMessage", message);
              navigate({ to: "/login" });
            } else {
              setIsVerifying(false);
            }
          },
          onError: () => {
            sessionStorage.setItem(
              "onboardingExpiredMessage",
              "Unable to verify link. Please try again or contact your administrator."
            );
            navigate({ to: "/login" });
          }
        }
      );
    } else {
      // No token provided - redirect to login with error
      sessionStorage.setItem(
        "onboardingExpiredMessage",
        "Invalid link. Please use the link from your email or log in with your password."
      );
      navigate({ to: "/login" });
    }
  }, [token, navigate]);

  // Handle successful password setup
  useEffect(() => {
    if (setupPassword.isSuccess && setupPassword.data) {
      const handleSuccess = async () => {
        try {
          // Store user info and token
          const { token: customToken, user } = setupPassword.data;

          // IMPORTANT: Clear any stale onboarding data from previous sessions
          // This ensures the instructions page form is not pre-populated or disabled
          // due to data from a different user's incomplete session
          clearOnboarding();
          sessionStorage.removeItem("onboardingData"); // Clear legacy storage key

          sessionStorage.setItem("onboardingUser", JSON.stringify(user));
          sessionStorage.setItem("onboardingToken", customToken);
          setUuid(user.uuid);

          // Sign in with Firebase custom token
          const userCredential = await signInWithCustomToken(auth, customToken);

          // IMPORTANT: Wait for ID token with claims before navigating
          // This fixes the race condition where roles aren't loaded yet
          // when the user navigates to the next page
          const idTokenResult =
            await userCredential.user.getIdTokenResult(true);
          const claims = idTokenResult.claims;
          const userRoles = (claims.roles as ContactRole[]) || [];

          // Set roles in store before navigating
          useUserStore.getState().setRoles(userRoles);

          // Navigate to onboarding
          navigate({ to: "/onboarding/workday" });
        } catch (error) {
          console.error("Error signing in after password setup:", error);
          setValidationError(
            "Password was set successfully, but there was an error signing in. Please go to the login page."
          );
        }
      };

      handleSuccess();
    }
  }, [setupPassword.isSuccess, setupPassword.data, navigate, setUuid]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setValidationError(null);

    // Validate passwords match
    if (password !== confirmPassword) {
      setValidationError("Passwords do not match");
      return;
    }

    // Validate password complexity
    const validation = validatePassword(password);
    if (!validation.isValid) {
      setValidationError(validation.errors[0]);
      return;
    }

    // Check for token
    if (!token) {
      setValidationError(
        "Invalid link. Please use the link from your email or contact support."
      );
      return;
    }

    // Submit password setup request
    setupPassword.mutate({ token, newPassword: password });
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  // Get user-friendly error message from API error
  const getApiErrorMessage = (error: Error | null): string => {
    if (!error) return "";

    const message = error.message || "An unexpected error occurred";

    // Common error patterns
    if (message.toLowerCase().includes("expired")) {
      return "This link has expired. Please contact your Rooster point of contact for a new invitation.";
    }
    if (message.toLowerCase().includes("invalid")) {
      return "This link is invalid. Please use the link from your email or contact support.";
    }
    if (message.toLowerCase().includes("not found")) {
      return "Account not found. Please contact support.";
    }
    if (
      message.toLowerCase().includes("already been set") ||
      message.toLowerCase().includes("already set")
    ) {
      return "Your password has already been set. Redirecting to login...";
    }

    return message;
  };

  // Handle "password already set" error by redirecting to login
  useEffect(() => {
    if (setupPassword.isError) {
      const errorMessage =
        (setupPassword.error as Error)?.message?.toLowerCase() || "";
      if (
        errorMessage.includes("already been set") ||
        errorMessage.includes("already set")
      ) {
        // Redirect to login page after a short delay
        const timer = setTimeout(() => {
          navigate({ to: "/login" });
        }, 2000);
        return () => clearTimeout(timer);
      }
    }
  }, [setupPassword.isError, setupPassword.error, navigate]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "100vh",
        padding: 3,
        background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
      }}
    >
      <Card
        sx={{
          maxWidth: 450,
          width: "100%",
          boxShadow: "lg"
        }}
      >
        <CardContent sx={{ p: 4 }}>
          {/* Show loading state while verifying the magic link token */}
          {isVerifying ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                py: 4
              }}
            >
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  border: "3px solid",
                  borderColor: "primary.200",
                  borderTopColor: "primary.500",
                  borderRadius: "50%",
                  animation: "spin 1s linear infinite",
                  "@keyframes spin": {
                    "0%": { transform: "rotate(0deg)" },
                    "100%": { transform: "rotate(360deg)" }
                  }
                }}
              />
              <Typography
                level="body-md"
                sx={{ mt: 2, color: "text.secondary" }}
              >
                Verifying your link...
              </Typography>
            </Box>
          ) : (
            <>
              <Box sx={{ textAlign: "center", mb: 3 }}>
                <Typography level="h2" component="h1" sx={{ mb: 1 }}>
                  Set Up Your Password
                </Typography>
                <Typography level="body-md" sx={{ color: "text.secondary" }}>
                  Create a secure password for your Rooster account
                </Typography>
              </Box>

              {/* Error alerts */}
              {(validationError || setupPassword.isError) && (
                <Alert color="danger" sx={{ mb: 2 }}>
                  {validationError ||
                    getApiErrorMessage(setupPassword.error as Error)}
                </Alert>
              )}

              {/* Missing token warning */}
              {!token && (
                <Alert color="warning" sx={{ mb: 2 }}>
                  Invalid or missing link. Please use the link from your email.
                </Alert>
              )}

              <form onSubmit={handleSubmit}>
                <FormControl sx={{ mb: 2 }}>
                  <FormLabel htmlFor="password">New Password</FormLabel>
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter your new password"
                    disabled={setupPassword.isPending}
                    endDecorator={
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={togglePasswordVisibility}
                        variant="plain"
                        size="sm"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    }
                    slotProps={{
                      input: {
                        autoComplete: "new-password"
                      }
                    }}
                  />
                  <FormHelperText>
                    At least 12 characters with uppercase, lowercase, number,
                    and special character
                  </FormHelperText>
                </FormControl>

                <FormControl sx={{ mb: 3 }}>
                  <FormLabel htmlFor="confirmPassword">
                    Confirm Password
                  </FormLabel>
                  <Input
                    id="confirmPassword"
                    type={showConfirmPassword ? "text" : "password"}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Confirm your new password"
                    disabled={setupPassword.isPending}
                    endDecorator={
                      <IconButton
                        aria-label="toggle confirm password visibility"
                        onClick={toggleConfirmPasswordVisibility}
                        variant="plain"
                        size="sm"
                      >
                        {showConfirmPassword ? (
                          <VisibilityOff />
                        ) : (
                          <Visibility />
                        )}
                      </IconButton>
                    }
                    slotProps={{
                      input: {
                        autoComplete: "new-password"
                      }
                    }}
                  />
                </FormControl>

                <Button
                  type="submit"
                  fullWidth
                  loading={setupPassword.isPending}
                  disabled={setupPassword.isPending || !token}
                  sx={{ mb: 2 }}
                >
                  {setupPassword.isPending ? "Setting up..." : "Set Password"}
                </Button>
              </form>

              <Typography
                level="body-sm"
                sx={{ textAlign: "center", color: "text.tertiary" }}
              >
                This link expires in 14 days. If you need a new link, please
                contact your Rooster point of contact.
              </Typography>
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

// Route definition for the setup-password page
export const setupPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/setup-password",
  component: SetupPassword,
  validateSearch: (search: Record<string, unknown>): SetupPasswordSearch => {
    return {
      token: typeof search.token === "string" ? search.token : undefined
    };
  }
});

export default SetupPassword;
